import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import { CLASE_OVERLAY } from './ui/clases';

/** Qué se va a resolver: una propuesta de condiciones o de suplente, por su ID. */
export interface ObjetivoResolucion {
  tipo: 'condicion' | 'suplente';
  id: number;
  accion: 'autorizar' | 'rechazar';
  /** «Condiciones de VAZ 1 · REGINA», «Suplente: PAULINA RÍOS (VAZ 1 · REGINA)». */
  titulo: string;
  /** Lo que se decide, tal como se va a autorizar: el regional autoriza lo que ve aquí. */
  resumen: React.ReactNode;
}

interface Props {
  objetivo: ObjetivoResolucion | null;
  onHide: () => void;
  onResuelto: (mensaje: string) => void;
  /**
   * 409: la propuesta ya no está abierta (el supervisor la reemplazó —cambia de
   * id—, la retiró o alguien más la resolvió) o, al autorizar, la VENTANA: ya
   * valdría en semanas cerradas. Quien abrió el diálogo RECARGA y avisa con el
   * mensaje de la API, que es el que dice cuál de las dos fue.
   */
  onConflicto: (mensaje: string, accion: 'autorizar' | 'rechazar') => void;
}

/**
 * Título del aviso de un 409 al resolver. Neutro a propósito: la API usa 409
 * para «la propuesta cambió» y para la ventana, y titular «La propuesta
 * cambió» a un 409 por ventana mandaba a buscar una propuesta nueva que no
 * existía. Lo que pasó lo dice el mensaje de la API.
 */
export const tituloNoResuelta = (accion: 'autorizar' | 'rechazar'): string =>
  accion === 'autorizar' ? 'No se pudo autorizar' : 'No se pudo rechazar';

/**
 * Autorizar o rechazar una propuesta del catálogo. Rechazar exige comentario
 * —la API también—: un rechazo sin decir qué corregir sólo produce la misma
 * propuesta otra vez. Autorizar lo admite, opcional.
 *
 * ⚠️ Sólo cuenta la respuesta del objetivo que sigue abierto (contador
 * `pedido`): avisar por uno que ya se cerró cerraría el siguiente.
 */
export const ResolverCatalogoDialog: React.FC<Props> = ({ objetivo, onHide, onResuelto, onConflicto }) => {
  const [comentario, setComentario] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pedido = useRef(0);

  useEffect(() => {
    pedido.current++;
    setComentario('');
    setError(null);
    setTrabajando(false);
  }, [objetivo]);

  const rechazar = objetivo?.accion === 'rechazar';
  const falta = rechazar && !comentario.trim();

  const resolver = async () => {
    if (!objetivo || falta) return;
    const n = pedido.current;
    const { tipo, id, accion, titulo } = objetivo;
    setTrabajando(true);
    setError(null);
    try {
      if (tipo === 'condicion') {
        if (accion === 'autorizar') await MetasColaboradorService.autorizarCondicion(id, comentario);
        else await MetasColaboradorService.rechazarCondicion(id, comentario);
      } else if (accion === 'autorizar') await MetasColaboradorService.autorizarSuplente(id, comentario);
      else await MetasColaboradorService.rechazarSuplente(id, comentario);
      if (n !== pedido.current) return;
      onResuelto(
        accion === 'autorizar' ? `${titulo}: autorizada.` : `${titulo}: rechazada, el supervisor verá tu comentario.`,
      );
    } catch (e) {
      if (n !== pedido.current) return;
      if (e instanceof MetasApiError && e.esConflicto) {
        onConflicto(e.message, accion);
        return;
      }
      setError((e as Error).message);
    } finally {
      if (n === pedido.current) setTrabajando(false);
    }
  };

  const pie = (
    <div className="flex flex-wrap justify-end gap-2">
      <Button label="Cancelar" text onClick={onHide} disabled={trabajando} />
      {rechazar ? (
        <Button
          label="Confirmar rechazo"
          icon="pi pi-times"
          severity="danger"
          disabled={falta || trabajando}
          loading={trabajando}
          onClick={() => void resolver()}
        />
      ) : (
        <Button
          label="Autorizar"
          icon="pi pi-check"
          severity="success"
          disabled={trabajando}
          loading={trabajando}
          onClick={() => void resolver()}
        />
      )}
    </div>
  );

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={objetivo ? `${rechazar ? 'Rechazar' : 'Autorizar'} · ${objetivo.titulo}` : ''}
      visible={!!objetivo}
      onHide={() => {
        if (!trabajando) onHide();
      }}
      footer={pie}
      style={{ width: '36rem', maxWidth: '95vw' }}
      modal
    >
      {objetivo && (
        <div className="flex flex-col gap-3">
          {objetivo.resumen}
          <div className="flex flex-col gap-1">
            <label htmlFor="catalogo-comentario" className="font-semibold">
              {rechazar ? 'Qué debe corregir el supervisor' : 'Comentario'}{' '}
              {!rechazar && <span className="font-normal text-gray-500">(opcional)</span>}
            </label>
            <InputTextarea
              id="catalogo-comentario"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={3}
              maxLength={1000}
              autoResize
              className="w-full"
            />
            {rechazar && falta && <small className="text-gray-600">Para rechazar hay que decir por qué.</small>}
          </div>
          {error && <Message severity="error" text={error} />}
        </div>
      )}
    </Dialog>
  );
};

export default ResolverCatalogoDialog;
